import React, {useState, useEffect} from 'react';
import styled from "styled-components";
import Loader from './Loader';
import {useQuery} from '../utils';

const PARAMS = {
  'FP_DEV': {
    scriptId: 19917108677,
    cloudUrl: 'https://us-central1-dev-form-publisher.cloudfunctions.net/pickedFileOrFolder',
    redeemUrl: 'https://us-central1-dev-form-publisher.cloudfunctions.net/redeemPickerToken'
  },
  'FP_STAGING': {
    scriptId: 810586556449,
    cloudUrl: 'https://us-central1-form-publisher-staging.cloudfunctions.net/pickedFileOrFolder',
    redeemUrl: 'https://us-central1-form-publisher-staging.cloudfunctions.net/redeemPickerToken'
  },
  'FP_PROD': {
    scriptId: 827172627657,
    cloudUrl: 'https://us-central1-form-publisher.cloudfunctions.net/pickedFileOrFolder',
    redeemUrl: 'https://us-central1-form-publisher.cloudfunctions.net/redeemPickerToken'
  }
};
const VIEWS = ['DOCUMENTS', 'SPREADSHEETS', 'PRESENTATIONS', 'FOLDERS'];

const useQueryParams = () => {
  const query = useQuery();
  const addonCode = query.get('addonCode');
  const nonce = query.get('nonce');
  // Legacy fallback: old add-on versions pass the raw OAuth token directly in the
  // URL instead of a nonce. Remove once all clients send a nonce (FPR-1113).
  const legacyAccessToken = query.get('accessToken');
  const target = query.get('target');
  const documentId = query.get('documentId');

  return [addonCode, nonce, legacyAccessToken, target, documentId];
};

function redeemToken(url, nonce, addonCode) {
  return fetch(url, {
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data: { nonce, addonCode } }),
  })
    .then((res) => res.json())
    .then((body) => {
      const accessToken = body && body.result && body.result.accessToken;
      if (!accessToken) {
        throw new Error((body && body.error && body.error.message) || 'Failed to redeem picker token');
      }
      return accessToken;
    });
}

function pickerCallback(data, {
  url,
  accessToken,
  target,
  documentId
}) {
  if (data.action === 'picked') {
    fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'post',
      body: JSON.stringify({
        data: {
          action: data.action,
          elements: data.docs.map(el => ({
            id: el.id,
            mimeType: el.mimeType,
            url: el.url,
          })),
          accessToken,
          target,
          documentId
        }
      })
    })
    .catch(console.error)
    .finally(() => {
      window.parent.postMessage(data, '*');
    });
  } else {
    window.parent.postMessage(data, '*');
  }
}

const buildPicker = (accessToken, { addonCode, target, documentId, onLoaded, onSaving }) => {
  /*global gapi*/
  gapi.load('picker', () => {
    /*global google*/
    const picker = new google.picker.PickerBuilder()
      .setAppId(PARAMS[addonCode].scriptId)
      .setOAuthToken(accessToken)
      .setCallback((data) => {
        onLoaded();
        if (data.action === 'picked' && onSaving) onSaving();
        pickerCallback(data, {
          url: PARAMS[addonCode].cloudUrl,
          accessToken,
          target,
          documentId
        });
      })
      .setSize(window.innerWidth, window.innerHeight)
      .setOrigin(window.location.protocol + '//' + window.location.host);

    VIEWS.forEach((view) => {
      if (view === 'FOLDERS') {
        picker.addView(
          new google.picker.DocsView(google.picker.ViewId.FOLDERS)
            .setIncludeFolders(true)
            .setSelectFolderEnabled(true)
        );
      } else {
        picker.addView(google.picker.ViewId[view]);
      }
    });

    picker.build().setVisible(true);
  });
};

const PickerWrappepr = styled.div`
  min-width: 100%;
  min-height: 100%;
`;

const MessageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100%;
  min-height: 100%;
  padding: 24px;
  text-align: center;
  font-family: Roboto, Arial, sans-serif;
  color: #5f6368;
`;

const Picker = () => {
  const [addonCode, nonce, legacyAccessToken, target, documentId] = useQueryParams();
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!(addonCode && PARAMS[addonCode] && (nonce || legacyAccessToken))) return;

    // Prefer the nonce flow; fall back to a raw OAuth token from the URL for
    // old add-on versions that haven't migrated yet (FPR-1113).
    const resolveToken = nonce
      ? redeemToken(PARAMS[addonCode].redeemUrl, nonce, addonCode)
      : Promise.resolve(legacyAccessToken);

    resolveToken
      .then((accessToken) => {
        buildPicker(accessToken, {
          addonCode,
          target,
          documentId,
          onLoaded: () => setPickerLoaded(true),
          onSaving: () => setSaving(true),
        });
      })
      .catch((err) => {
        console.error('Could not start the picker:', err);
        setFailed(true);
        // Return the user to the add-on so they can reopen the picker and get a fresh nonce.
        setTimeout(() => window.parent.postMessage({ action: 'cancel' }, '*'), 4000);
      });
  }, []);

  if (failed) {
    return (<MessageWrapper>
      Your picker session expired. Please close this window and open the picker again.
    </MessageWrapper>);
  }

  if (saving) {
    return (<MessageWrapper>
      <Loader inline width="24px" height="24px" />
      <span style={{ marginLeft: 12 }}>Saving your selection…</span>
    </MessageWrapper>);
  }

  return (<PickerWrappepr>
    {!pickerLoaded && <Loader width="100px" height="100px" />}
  </PickerWrappepr>);
};

export default Picker;
